// ===================================================================
// HMGP — FEMA Forms & Packages
// PDF generation for HMGP subapplication packages, project summaries,
// cost breakdowns, and bulk CSV export
// ===================================================================

import { useState, useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Button, Select, SimpleGrid, Center,
  Loader, Alert, ThemeIcon, Badge,
} from '../../../components/gab/GabComponents';
import {
  IconFileTypePdf, IconFileSpreadsheet, IconDownload, IconClipboardList,
  IconCurrencyDollar, IconReportAnalytics, IconCalendarStats, IconAlertCircle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { exportToCSV } from '../../../lib/exportUtils';
import {
  HMGP_PROJECT_TYPE_LABELS,
  HMGP_PHASE_LABELS,
  HMGP_STATUS_LABELS,
  type HMGPProject,
  type HMGPSubapplication,
  type PAOrganization,
  type PADisaster,
  type PAProjectCost,
} from '../../../types/pagrants';

// ── Helpers ──────────────────────────────────────────────────

function num(v: string | number): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

function fmtCurrency(v: string | number): string {
  const n = num(v);
  if (n === 0) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function fmtDate(v: string): string {
  if (!v) return 'N/A';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US');
}

function today(): string {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── PDF Footer ───────────────────────────────────────────────

function addFooter(doc: jsPDF, pageNum: number, totalPages: number) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated ${today()}`, 40, h - 20);
  doc.text(`Page ${pageNum} of ${totalPages}`, w - 40, h - 20, { align: 'right' });
}

function addFooters(doc: jsPDF) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    addFooter(doc, i, total);
  }
}

// Wrapped text helper — returns final Y after drawing
function drawWrapped(doc: jsPDF, text: string, x: number, y: number, maxW: number, lineH: number): number {
  if (!text) return y;
  const lines: string[] = doc.splitTextToSize(text, maxW);
  doc.text(lines, x, y);
  return y + lines.length * lineH;
}

// ── Section Header ───────────────────────────────────────────

function sectionHeader(doc: jsPDF, title: string, y: number): number {
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(5, 150, 105); // #059669
  doc.text(title, 40, y);
  doc.setDrawColor(5, 150, 105);
  doc.line(40, y + 3, doc.internal.pageSize.getWidth() - 40, y + 3);
  doc.setTextColor(0, 0, 0);
  return y + 16;
}

// ── Check if we need a new page ─────────────────────────────

function checkPage(doc: jsPDF, y: number, needed: number = 40): number {
  if (y + needed > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    return 40;
  }
  return y;
}

// ═══════════════════════════════════════════════════════════════
// 1. HMGP Subapplication Package
// ═══════════════════════════════════════════════════════════════

function generateSubappPackage(
  project: HMGPProject,
  subapp: HMGPSubapplication | null,
  org: PAOrganization | null,
  disaster: PADisaster,
  costs: PAProjectCost[],
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const w = doc.internal.pageSize.getWidth();
  const margin = 40;
  const contentW = w - margin * 2;

  // ── Cover Page ──────────────────────────────────────────
  let y = 100;
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(5, 150, 105);
  doc.text('HMGP Subapplication Package', w / 2, y, { align: 'center' });

  y += 40;
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text(project.projectTitle || 'Untitled Project', w / 2, y, { align: 'center' });

  y += 30;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Project #: ${project.projectNumber || 'N/A'}`, w / 2, y, { align: 'center' });
  y += 16;
  doc.text(`FEMA App #: ${project.femaAppNumber || 'Pending'}`, w / 2, y, { align: 'center' });

  y += 40;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin + 60, y, w - margin - 60, y);

  // Disaster info
  y += 30;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Disaster Information', margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.text(`Disaster: ${disaster.disasterName} (${disaster.disasterNumber || 'N/A'})`, margin, y);
  y += 14;
  doc.text(`Declaration: ${disaster.declarationType || 'N/A'} — ${fmtDate(disaster.declarationDate)}`, margin, y);
  y += 14;
  doc.text(`State: ${disaster.state || 'N/A'}`, margin, y);

  // Applicant info
  y += 30;
  doc.setFont('helvetica', 'bold');
  doc.text('Applicant Information', margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  if (org) {
    doc.text(`Organization: ${org.orgName}`, margin, y);
    y += 14;
    doc.text(`Type: ${org.orgType || 'N/A'}  |  FEMA ID: ${org.femaApplicantId || 'N/A'}`, margin, y);
    y += 14;
    doc.text(`UEI: ${org.ueiNumber || 'N/A'}  |  FIPS: ${org.fipsCode || 'N/A'}`, margin, y);
    y += 14;
    doc.text(`Contact: ${org.primaryContact || 'N/A'}  (${org.primaryEmail || 'N/A'})`, margin, y);
  } else {
    doc.text('Organization: N/A', margin, y);
  }

  // Project info
  y += 30;
  doc.setFont('helvetica', 'bold');
  doc.text('Project Summary', margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  const typeLbl = HMGP_PROJECT_TYPE_LABELS[project.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || project.projectType || 'N/A';
  doc.text(`Type: ${typeLbl}`, margin, y);
  y += 14;
  doc.text(`Location: ${[project.projectAddress, project.projectCity, project.projectState, project.projectZip].filter(Boolean).join(', ') || 'N/A'}`, margin, y);
  y += 14;
  doc.text(`County: ${project.projectCounty || 'N/A'}`, margin, y);
  y += 14;
  doc.text(`Estimated Cost: ${fmtCurrency(project.estimatedCost)}`, margin, y);

  // ── Section 1: Project Description ──────────────────────
  doc.addPage();
  y = 40;
  y = sectionHeader(doc, 'Section 1: Project Description', y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Problem Statement', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  const problem = subapp?.problemStatement || project.problemDescription || 'No problem statement provided.';
  y = drawWrapped(doc, problem, margin, y, contentW, 13);

  y += 16;
  y = checkPage(doc, y, 60);
  doc.setFont('helvetica', 'bold');
  doc.text('Proposed Activity / Scope of Work', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  const scope = subapp?.proposedActivity || project.scopeOfWork || 'No proposed activity described.';
  y = drawWrapped(doc, scope, margin, y, contentW, 13);

  // ── Section 2: Project Budget ───────────────────────────
  y += 20;
  y = checkPage(doc, y, 100);
  y = sectionHeader(doc, 'Section 2: Project Budget', y);

  const estCost = num(project.estimatedCost);
  const fedPct = num(project.federalSharePct) || 75;
  const fedAmt = num(project.federalShareAmount) || estCost * (fedPct / 100);
  const appAmt = num(project.applicantShareAmount) || estCost - fedAmt;
  const otherFund = num(project.otherFunding);

  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  const budgetLines = [
    ['Total Estimated Cost:', fmtCurrency(estCost)],
    [`Federal Share (${fedPct}%):`, fmtCurrency(fedAmt)],
    ['Applicant Match:', fmtCurrency(appAmt)],
  ];
  if (otherFund > 0) {
    budgetLines.push([`Other Funding (${project.otherFundingSource || 'N/A'}):`, fmtCurrency(otherFund)]);
  }
  for (const [lbl, val] of budgetLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 280, y);
    y += 14;
  }

  // Cost line items
  if (costs.length > 0) {
    y += 10;
    y = checkPage(doc, y, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Cost Line Items', margin, y);
    y += 16;

    // Table header
    doc.setFont('courier', 'bold');
    doc.setFontSize(9);
    doc.text('Description', margin + 10, y);
    doc.text('Type', margin + 230, y);
    doc.text('Qty', margin + 310, y);
    doc.text('Unit Cost', margin + 350, y);
    doc.text('Total', margin + 430, y);
    y += 4;
    doc.setDrawColor(180, 180, 180);
    doc.line(margin + 10, y, w - margin, y);
    y += 12;

    doc.setFont('courier', 'normal');
    let grandTotal = 0;
    for (const c of costs) {
      y = checkPage(doc, y, 16);
      const desc = (c.description || '').substring(0, 35);
      doc.text(desc, margin + 10, y);
      doc.text((c.costType || '').substring(0, 12), margin + 230, y);
      doc.text(c.quantity || '', margin + 310, y);
      doc.text(fmtCurrency(c.unitCost), margin + 350, y);
      doc.text(fmtCurrency(c.totalCost), margin + 430, y);
      grandTotal += num(c.totalCost);
      y += 13;
    }
    y += 4;
    doc.line(margin + 10, y, w - margin, y);
    y += 14;
    doc.setFont('courier', 'bold');
    doc.text('Grand Total:', margin + 310, y);
    doc.text(fmtCurrency(grandTotal), margin + 430, y);
  }

  // ── Section 3: Benefit-Cost Analysis ────────────────────
  doc.addPage();
  y = 40;
  y = sectionHeader(doc, 'Section 3: Benefit-Cost Analysis Summary', y);

  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  const bcaLines: [string, string][] = [
    ['BCA Required:', project.bcaRequired || 'N/A'],
    ['Methodology:', project.bcaMethodology || 'N/A'],
    ['Benefit-Cost Ratio (BCR):', project.bcaRatio || 'N/A'],
    ['PV Benefits:', project.pvBenefits ? fmtCurrency(project.pvBenefits) : 'N/A'],
    ['PV Costs:', project.pvCosts ? fmtCurrency(project.pvCosts) : 'N/A'],
    ['Useful Life (years):', project.usefulLife || 'N/A'],
    ['Discount Rate:', project.discountRate ? `${project.discountRate}%` : 'N/A'],
    ['BCA Tool Version:', project.bcaToolVersion || 'N/A'],
    ['BCA Completed Date:', fmtDate(project.bcaCompletedDate)],
  ];
  for (const [lbl, val] of bcaLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 260, y);
    y += 14;
  }

  if (num(project.bcaRatio) > 0 && num(project.bcaRatio) < 1) {
    y += 6;
    doc.setTextColor(200, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('WARNING: BCR is below 1.0 — project may not be cost-effective.', margin + 10, y);
    doc.setTextColor(0, 0, 0);
  }

  // ── Section 4: Environmental / Historic Preservation ────
  y += 30;
  y = checkPage(doc, y, 80);
  y = sectionHeader(doc, 'Section 4: Environmental / Historic Preservation', y);

  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  const ehpLines: [string, string][] = [
    ['EHP Status:', project.ehpStatus || 'Pending'],
    ['EHP Completed Date:', fmtDate(project.ehpCompletedDate)],
    ['NEPA Level:', project.nepaLevel || 'N/A'],
    ['NEPA Status:', project.nepaStatus || 'N/A'],
    ['Section 106 Required:', project.section106Required || 'N/A'],
    ['Section 106 Status:', project.section106Status || 'N/A'],
  ];
  for (const [lbl, val] of ehpLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 260, y);
    y += 14;
  }

  // ── Section 5: Project Schedule ─────────────────────────
  y += 20;
  y = checkPage(doc, y, 80);
  y = sectionHeader(doc, 'Section 5: Project Schedule', y);

  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  const schedLines: [string, string][] = [
    ['Performance Period Start:', fmtDate(project.performancePeriodStart)],
    ['Performance Period End:', fmtDate(project.performancePeriodEnd)],
    ['Quarterly Report Due:', fmtDate(project.quarterlyReportDue)],
    ['Phase:', HMGP_PHASE_LABELS[project.phase as keyof typeof HMGP_PHASE_LABELS] || project.phase || 'N/A'],
    ['Status:', HMGP_STATUS_LABELS[project.status as keyof typeof HMGP_STATUS_LABELS] || project.status || 'N/A'],
  ];
  for (const [lbl, val] of schedLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 260, y);
    y += 14;
  }

  // ── Section 6: Property Details ─────────────────────────
  const propType = project.projectType;
  if (propType === 'acquisition' || propType === 'elevation') {
    y += 20;
    y = checkPage(doc, y, 80);
    y = sectionHeader(doc, 'Section 6: Property Details (Acquisition/Elevation)', y);

    doc.setFontSize(10);
    doc.setFont('courier', 'normal');
    const propLines: [string, string][] = [
      ['Number of Structures:', project.numberOfStructures || 'N/A'],
      ['Structure Type:', project.structureType || 'N/A'],
      ['Pre-Disaster Value:', project.preDisasterValue ? fmtCurrency(project.preDisasterValue) : 'N/A'],
      ['Flood Zone:', project.floodZone || 'N/A'],
      ['Repetitive Loss:', project.repetitiveLoss || 'N/A'],
      ['Severe Repetitive Loss:', project.severeRepetitiveLoss || 'N/A'],
    ];
    for (const [lbl, val] of propLines) {
      doc.text(lbl, margin + 10, y);
      doc.text(val, margin + 260, y);
      y += 14;
    }
  }

  addFooters(doc);
  doc.save(`HMGP_Subapplication_${project.projectNumber || project.Rid}.pdf`);
}

// ═══════════════════════════════════════════════════════════════
// 2. Project Summary Report (one page)
// ═══════════════════════════════════════════════════════════════

function generateProjectSummary(
  project: HMGPProject,
  org: PAOrganization | null,
  disaster: PADisaster,
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const w = doc.internal.pageSize.getWidth();
  const margin = 40;

  let y = 40;

  // Title bar
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, w, 50, 'F');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('HMGP Project Summary', margin, 32);

  y = 70;
  doc.setTextColor(0, 0, 0);

  // Project Header
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(project.projectTitle || 'Untitled Project', margin, y);
  y += 18;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const typeLbl = HMGP_PROJECT_TYPE_LABELS[project.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || project.projectType || '';
  doc.text(`#${project.projectNumber || 'N/A'}  |  ${typeLbl}  |  ${HMGP_PHASE_LABELS[project.phase as keyof typeof HMGP_PHASE_LABELS] || project.phase || 'N/A'}  |  ${HMGP_STATUS_LABELS[project.status as keyof typeof HMGP_STATUS_LABELS] || project.status || 'N/A'}`, margin, y);
  y += 14;
  doc.text(`Disaster: ${disaster.disasterName} (${disaster.disasterNumber || 'N/A'})`, margin, y);

  // Divider
  y += 14;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, w - margin, y);
  y += 16;

  // Financial Summary
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Financial Summary', margin, y);
  y += 16;

  doc.setFont('courier', 'normal');
  doc.setFontSize(10);
  const estCost = num(project.estimatedCost);
  const fedPct = num(project.federalSharePct) || 75;
  const fedAmt = num(project.federalShareAmount) || estCost * (fedPct / 100);
  const appAmt = num(project.applicantShareAmount) || estCost - fedAmt;
  const awarded = num(project.awardAmount);
  const obligated = num(project.obligatedAmount);
  const drawn = num(project.drawdownAmount);

  const finLines: [string, string][] = [
    ['Estimated Cost:', fmtCurrency(estCost)],
    [`Federal Share (${fedPct}%):`, fmtCurrency(fedAmt)],
    ['Applicant Match:', fmtCurrency(appAmt)],
    ['Award Amount:', fmtCurrency(awarded)],
    ['Obligated Amount:', fmtCurrency(obligated)],
    ['Drawdown Amount:', fmtCurrency(drawn)],
  ];
  for (const [lbl, val] of finLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 250, y);
    y += 14;
  }

  // BCA Quick Stats
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('BCA Quick Stats', margin, y);
  y += 16;
  doc.setFont('courier', 'normal');
  doc.setFontSize(10);
  doc.text(`BCR: ${project.bcaRatio || 'N/A'}`, margin + 10, y);
  doc.text(`Methodology: ${project.bcaMethodology || 'N/A'}`, margin + 180, y);
  y += 14;
  doc.text(`PV Benefits: ${project.pvBenefits ? fmtCurrency(project.pvBenefits) : 'N/A'}`, margin + 10, y);
  doc.text(`PV Costs: ${project.pvCosts ? fmtCurrency(project.pvCosts) : 'N/A'}`, margin + 260, y);

  // Key Dates
  y += 24;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Key Dates', margin, y);
  y += 16;
  doc.setFont('courier', 'normal');
  doc.setFontSize(10);
  const dateLines: [string, string][] = [
    ['Subapplication Date:', fmtDate(project.subapplicationDate)],
    ['FEMA Award Date:', fmtDate(project.femaAwardDate)],
    ['Period Start:', fmtDate(project.performancePeriodStart)],
    ['Period End:', fmtDate(project.performancePeriodEnd)],
    ['Quarterly Report Due:', fmtDate(project.quarterlyReportDue)],
  ];
  for (const [lbl, val] of dateLines) {
    doc.text(lbl, margin + 10, y);
    doc.text(val, margin + 250, y);
    y += 14;
  }

  // Organization Info
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Organization', margin, y);
  y += 16;
  doc.setFont('courier', 'normal');
  doc.setFontSize(10);
  if (org) {
    doc.text(`Name: ${org.orgName}`, margin + 10, y); y += 14;
    doc.text(`Type: ${org.orgType || 'N/A'}  |  FEMA ID: ${org.femaApplicantId || 'N/A'}`, margin + 10, y); y += 14;
    doc.text(`Contact: ${org.primaryContact || 'N/A'}  |  ${org.primaryEmail || 'N/A'}`, margin + 10, y); y += 14;
    doc.text(`Phone: ${org.primaryPhone || 'N/A'}`, margin + 10, y);
  } else {
    doc.text('Organization: N/A', margin + 10, y);
  }

  addFooters(doc);
  doc.save(`HMGP_Summary_${project.projectNumber || project.Rid}.pdf`);
}

// ═══════════════════════════════════════════════════════════════
// 3. Cost Summary Package
// ═══════════════════════════════════════════════════════════════

function generateCostSummary(
  project: HMGPProject,
  disaster: PADisaster,
  costs: PAProjectCost[],
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const w = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Header
  let y = 40;
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, w, 50, 'F');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('HMGP Cost Summary', margin, 32);

  y = 70;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.text(`Project: ${project.projectTitle || 'Untitled'}  (#${project.projectNumber || 'N/A'})`, margin, y);
  y += 14;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Disaster: ${disaster.disasterName}  |  Type: ${HMGP_PROJECT_TYPE_LABELS[project.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || project.projectType || 'N/A'}`, margin, y);

  y += 24;
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, w - margin, y);
  y += 16;

  if (costs.length === 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text('No cost line items have been recorded for this project.', margin, y);
    addFooters(doc);
    doc.save(`HMGP_CostSummary_${project.projectNumber || project.Rid}.pdf`);
    return;
  }

  // Group by costType
  const grouped: Record<string, PAProjectCost[]> = {};
  for (const c of costs) {
    const t = c.costType || 'Uncategorized';
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(c);
  }

  doc.setFont('courier', 'normal');
  doc.setFontSize(9);

  let grandTotal = 0;

  for (const [type, items] of Object.entries(grouped)) {
    y = checkPage(doc, y, 60);

    // Type header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(type.charAt(0).toUpperCase() + type.slice(1), margin, y);
    y += 14;

    // Column headers
    doc.setFont('courier', 'bold');
    doc.setFontSize(9);
    doc.text('Description', margin + 10, y);
    doc.text('Qty', margin + 250, y);
    doc.text('Unit', margin + 290, y);
    doc.text('Unit Cost', margin + 340, y);
    doc.text('Total', margin + 430, y);
    y += 4;
    doc.line(margin + 10, y, w - margin, y);
    y += 12;

    doc.setFont('courier', 'normal');
    let subtotal = 0;
    for (const c of items) {
      y = checkPage(doc, y, 16);
      const desc = (c.description || '').substring(0, 38);
      doc.text(desc, margin + 10, y);
      doc.text(c.quantity || '', margin + 250, y);
      doc.text((c.unit || '').substring(0, 8), margin + 290, y);
      doc.text(fmtCurrency(c.unitCost), margin + 340, y);
      doc.text(fmtCurrency(c.totalCost), margin + 430, y);
      subtotal += num(c.totalCost);
      y += 13;
    }

    // Subtotal
    y += 2;
    doc.setFont('courier', 'bold');
    doc.text(`Subtotal (${type}):`, margin + 290, y);
    doc.text(fmtCurrency(subtotal), margin + 430, y);
    grandTotal += subtotal;
    y += 20;
  }

  // Grand total + federal/applicant
  y = checkPage(doc, y, 60);
  doc.setDrawColor(5, 150, 105);
  doc.line(margin, y, w - margin, y);
  y += 16;

  const fedPct = num(project.federalSharePct) || 75;

  doc.setFont('courier', 'bold');
  doc.setFontSize(11);
  doc.text('Grand Total:', margin + 290, y);
  doc.text(fmtCurrency(grandTotal), margin + 430, y);
  y += 16;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Federal Share (${fedPct}%):`, margin + 290, y);
  doc.text(fmtCurrency(grandTotal * fedPct / 100), margin + 430, y);
  y += 14;
  doc.text(`Applicant Match (${100 - fedPct}%):`, margin + 290, y);
  doc.text(fmtCurrency(grandTotal * (100 - fedPct) / 100), margin + 430, y);

  addFooters(doc);
  doc.save(`HMGP_CostSummary_${project.projectNumber || project.Rid}.pdf`);
}

// ═══════════════════════════════════════════════════════════════
// 4. Bulk CSV Export
// ═══════════════════════════════════════════════════════════════

function exportAllProjectsCSV(
  projects: HMGPProject[],
  orgs: PAOrganization[],
  disaster: PADisaster,
) {
  const orgMap = new Map(orgs.map(o => [String(o.Rid), o]));
  const headers = ['Project #','Title','Type','Phase','Status','Organization','Address','County',
    'Estimated Cost','Federal Share %','Federal Share $','Applicant Match','Award Amount','Obligated','Drawdown',
    'BCR','BCA Methodology','EHP Status','NEPA Level','Period Start','Period End','FEMA App #','FEMA Award Date',
    'State Priority Rank','Specialist','Created'];
  const rows = projects.map(p => {
    const o = orgMap.get(String(p.organizationId));
    return [
      p.projectNumber, p.projectTitle,
      HMGP_PROJECT_TYPE_LABELS[p.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || p.projectType,
      HMGP_PHASE_LABELS[p.phase as keyof typeof HMGP_PHASE_LABELS] || p.phase,
      HMGP_STATUS_LABELS[p.status as keyof typeof HMGP_STATUS_LABELS] || p.status,
      o?.orgName || '', [p.projectAddress, p.projectCity, p.projectState, p.projectZip].filter(Boolean).join(', '),
      p.projectCounty, p.estimatedCost, p.federalSharePct, p.federalShareAmount, p.applicantShareAmount,
      p.awardAmount, p.obligatedAmount, p.drawdownAmount, p.bcaRatio, p.bcaMethodology,
      p.ehpStatus, p.nepaLevel, p.performancePeriodStart, p.performancePeriodEnd,
      p.femaAppNumber, p.femaAwardDate, p.statePriorityRank, p.assignedSpecialist, p.createdDate,
    ];
  });
  exportToCSV(`HMGP_Projects_${disaster.disasterNumber || 'export'}.csv`, headers, rows);
}

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

export default function HMGPFemaForms() {
  const navigate = useNavigate();
  const { projects, subapplications, fetchProjects, fetchSubapplications } = useHMGPStore();
  const { organizations, projectCosts, fetchOrganizations, fetchProjectCosts } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetchProjects(),
      fetchSubapplications(),
      fetchOrganizations(),
      fetchProjectCosts(),
    ]).finally(() => setLoading(false));
  }, []);

  // Filter projects to active disaster
  const filteredProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  const projectOptions = useMemo(() =>
    filteredProjects.map(p => ({
      value: String(p.Rid),
      label: `${p.projectNumber || 'No #'} — ${p.projectTitle || 'Untitled'}`,
    })),
  [filteredProjects]);

  const selectedProject = useMemo(() =>
    filteredProjects.find(p => String(p.Rid) === selectedProjectId) || null,
  [filteredProjects, selectedProjectId]);

  const selectedOrg = useMemo(() => {
    if (!selectedProject) return null;
    return organizations.find(o => String(o.Rid) === String(selectedProject.organizationId)) || null;
  }, [selectedProject, organizations]);

  const selectedSubapp = useMemo(() => {
    if (!selectedProject) return null;
    return subapplications.find(s => String(s.linkedProjectId) === String(selectedProject.Rid)) || null;
  }, [selectedProject, subapplications]);

  const selectedCosts = useMemo(() => {
    if (!selectedProject) return [];
    return projectCosts.filter(c => String(c.projectId) === String(selectedProject.Rid));
  }, [selectedProject, projectCosts]);

  // ── Loading ─────────────────────────────────────────────
  if (loading) {
    return <Center py={80}><Loader color="#059669" /></Center>;
  }

  // ── No disaster ─────────────────────────────────────────
  if (!activeDisaster) {
    return (
      <Stack gap="md">
        <Title order={3}>HMGP Forms &amp; Packages</Title>
        <Alert icon={<IconAlertCircle size={18} />} color="yellow" title="No Active Disaster">
          Select an incident linked to an HMGP disaster to generate forms and packages.
        </Alert>
      </Stack>
    );
  }

  // ── No projects ─────────────────────────────────────────
  if (filteredProjects.length === 0) {
    return (
      <Stack gap="md">
        <Title order={3}>HMGP Forms &amp; Packages</Title>
        <Text size="sm" c="dimmed">Generate official FEMA application packages for HMGP projects.</Text>
        <Alert icon={<IconAlertCircle size={18} />} color="yellow" title="No HMGP Projects">
          No HMGP projects found for disaster "{activeDisaster.disasterName}". Create a project first.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={3}>HMGP Forms &amp; Packages</Title>
          <Text size="sm" c="dimmed">Generate official FEMA application packages for HMGP projects.</Text>
        </div>
        <Button
          variant="light"
          color="teal"
          leftSection={<IconFileSpreadsheet size={16} />}
          onClick={() => exportAllProjectsCSV(filteredProjects, organizations, activeDisaster)}
        >
          Export All Projects (CSV)
        </Button>
      </Group>

      {/* Project selector */}
      <Paper p="md" withBorder>
        <Select
          label="Select Project"
          placeholder="Choose an HMGP project..."
          data={projectOptions}
          value={selectedProjectId}
          onChange={setSelectedProjectId}
          searchable
          clearable
          styles={{ label: { fontWeight: 600 } }}
        />
        {selectedProject && (
          <Group mt="sm" gap="xs">
            <Badge color="teal" variant="light">
              {HMGP_PROJECT_TYPE_LABELS[selectedProject.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || selectedProject.projectType}
            </Badge>
            <Badge color="gray" variant="light">
              {HMGP_PHASE_LABELS[selectedProject.phase as keyof typeof HMGP_PHASE_LABELS] || selectedProject.phase}
            </Badge>
            <Badge color="blue" variant="light">
              {HMGP_STATUS_LABELS[selectedProject.status as keyof typeof HMGP_STATUS_LABELS] || selectedProject.status}
            </Badge>
            <Text size="xs" c="dimmed">Est. Cost: {fmtCurrency(selectedProject.estimatedCost)}</Text>
          </Group>
        )}
      </Paper>

      {/* Package cards */}
      {selectedProject && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {/* Subapplication Package */}
          <Paper p="lg" withBorder style={{ borderTop: '3px solid #059669' }}>
            <Group gap="sm" mb="xs">
              <ThemeIcon color="teal" variant="light" size="lg">
                <IconClipboardList size={20} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Subapplication Package</Text>
                <Text size="xs" c="dimmed">Comprehensive FEMA application PDF</Text>
              </div>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Cover page, project description, budget with cost line items, BCA summary,
              environmental/historic preservation, schedule, and property details.
            </Text>
            <Button
              fullWidth
              color="teal"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => generateSubappPackage(selectedProject, selectedSubapp, selectedOrg, activeDisaster, selectedCosts)}
            >
              Generate PDF
            </Button>
          </Paper>

          {/* Project Summary */}
          <Paper p="lg" withBorder style={{ borderTop: '3px solid #059669' }}>
            <Group gap="sm" mb="xs">
              <ThemeIcon color="teal" variant="light" size="lg">
                <IconReportAnalytics size={20} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Project Summary Report</Text>
                <Text size="xs" c="dimmed">One-page project overview PDF</Text>
              </div>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Project header, financial summary, BCA quick stats, key dates, and
              organization information on a single page.
            </Text>
            <Button
              fullWidth
              color="teal"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => generateProjectSummary(selectedProject, selectedOrg, activeDisaster)}
            >
              Generate PDF
            </Button>
          </Paper>

          {/* Cost Summary */}
          <Paper p="lg" withBorder style={{ borderTop: '3px solid #059669' }}>
            <Group gap="sm" mb="xs">
              <ThemeIcon color="teal" variant="light" size="lg">
                <IconCurrencyDollar size={20} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Cost Summary Package</Text>
                <Text size="xs" c="dimmed">Itemized cost breakdown PDF</Text>
              </div>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Cost line items grouped by type with subtotals, grand total, and
              federal share / applicant match calculations.
            </Text>
            <Button
              fullWidth
              color="teal"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => generateCostSummary(selectedProject, activeDisaster, selectedCosts)}
              disabled={selectedCosts.length === 0}
            >
              {selectedCosts.length === 0 ? 'No Cost Items' : 'Generate PDF'}
            </Button>
          </Paper>

          {/* Quarterly Report — navigates */}
          <Paper p="lg" withBorder style={{ borderTop: '3px solid #059669' }}>
            <Group gap="sm" mb="xs">
              <ThemeIcon color="teal" variant="light" size="lg">
                <IconCalendarStats size={20} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">Quarterly Report Package</Text>
                <Text size="xs" c="dimmed">Performance period reporting</Text>
              </div>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Navigate to the quarterly reporting page to create, review, and export
              quarterly performance reports for FEMA.
            </Text>
            <Button
              fullWidth
              variant="outline"
              color="teal"
              leftSection={<IconDownload size={16} />}
              onClick={() => navigate('/pagrants/hmgp/quarterly')}
            >
              Go to Quarterly Reports
            </Button>
          </Paper>
        </SimpleGrid>
      )}
    </Stack>
  );
}
